import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, Eye, Calendar, User } from 'lucide-react';

// Utility to format Date and Time for display (reused from organizer)
const formatDateTime = (date, time) => {
  // Assuming date is in YYYY-MM-DD format and time is HH:MM
  const combinedString = `${String(date).split('T')[0]}T${time}`;
  const dateTimeObject = new Date(combinedString);

  return dateTimeObject.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    // hours: 'numeric',
    // minutes: 'numeric',
  });
};


// Mock Data structure reflecting the needed fields from Event and RSVP models
const MOCK_GUEST_INVITATIONS = [
  { 
    event_id: "i456-def-101", 
    event_title: "Holiday Gala Dinner", 
    host_name: "Smith Corp", 
    event_date: "2025-12-24",
    event_time: "19:00",
    image_url: "https://placehold.co/600x400/C42080/FFFFFF?text=Gala+Dinner",
    rsvp_status: "pending", 
    is_past: false, 
    invitation_link: "/invite/i456-def-101/user-token-123" 
  },
  { 
    event_id: "i456-def-102", 
    event_title: "Jane's Birthday Brunch", 
    host_name: "Jane Doe", 
    event_date: "2026-01-01",
    event_time: "11:00",
    image_url: "https://placehold.co/600x400/00BFA5/FFFFFF?text=Brunch+Party",
    rsvp_status: "yes", 
    is_past: false,
    invitation_link: "/invite/i456-def-102/user-token-123" 
  },
  { 
    event_id: "i456-def-103", 
    event_title: "Halloween Bash 2024", 
    host_name: "The Neighbors", 
    event_date: "2024-10-31",
    event_time: "20:00",
    image_url: "https://placehold.co/600x400/777777/FFFFFF?text=Halloween+Bash",
    rsvp_status: "no", 
    is_past: true,
    invitation_link: "/invite/i456-def-103/user-token-123" 
  },
];

// Helper to determine status tag class based on RSVP status
const getRsvpClass = (status) => {
  if (status === 'yes') return 'attending';
  if (status === 'pending') return 'pending';
  if (status === 'no') return 'declined';
  return 'pending';
};

// Helper to determine status display text
const getRsvpText = (status) => {
  if (status === 'yes') return 'ATTENDING';
  if (status === 'pending') return 'PENDING';
  if (status === 'no') return 'DECLINED';
  return 'UNKNOWN';
};

const InvitationCardGuest = ({ invitation }) => {
  const navigate = useNavigate();

  const handleRsvp = () => {
    // Redirects the user to the existing invite landing page 
    // navigate(invitation.invitation_link);
    navigate(`/invite/${invitation.invitation_link}`);
  };
  
  const displayDate = formatDateTime(invitation.event_date, invitation.event_time);
  const rsvpText = getRsvpText(invitation.rsvp_status);

  return (
    <div className={`invitation-card ${invitation.is_past ? 'past-card' : ''}`}>
      <div className="card-image-wrapper">
        <img 
          src={invitation.image_url} 
          alt={`${invitation.event_title} cover`} 
          className="card-cover-image" 
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x400/999999/FFFFFF?text=No+Image"; }}
        />
        <span className={`status-tag ${getRsvpClass(invitation.rsvp_status)}`}>
          {rsvpText}
        </span>
      </div>

      <div className="card-content">
        <div className="card-header">
          <h3>{invitation.event_title}</h3>
        </div>

        <p className="card-date-time"><Calendar size={14} style={{ marginRight: '5px' }} />{displayDate}</p>
        <p className="card-host"><User size={14} style={{ marginRight: '5px' }} />Hosted by: {invitation.host_name}</p>


        <div className="card-actions">
          {/* Single adaptive button for the main action */}
          <button className="hub-button primary" onClick={handleRsvp}>
            {invitation.rsvp_status === 'pending' && !invitation.is_past ? (
              <><CalendarCheck size={20} style={{ marginRight: '8px' }} /> RSVP Now!</>
            ) : (
              <><Eye size={20} style={{ marginRight: '8px' }} /> View Invitation</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const InvitationsOrganizer = ({invites}) => {
  const allInvitations = Array.isArray(invites) ? invites : MOCK_GUEST_INVITATIONS;
  // const allInvitations = MOCK_GUEST_INVITATIONS; 
  const [showPast, setShowPast] = useState(false);

  const activeInvitations = allInvitations.filter(i => !i.is_past);
  const pastInvitations = allInvitations.filter(i => i.is_past);

  return (
    <div>
      <div className="event-list-view">
        {activeInvitations.map(invitation => (
          <InvitationCardGuest key={invitation.event_id} invitation={invitation} />
        ))}
      </div>

      {/* Past Invitations Toggle */}
      {pastInvitations.length > 0 && (
        <button 
          className="past-toggle-button"
          onClick={() => setShowPast(!showPast)}
        >
          {showPast ? 'Hide Past Invitations' : `Show ${pastInvitations.length} Past Invitations`}
        </button>
      )}

      {/* Past Invitations List */}
      {showPast && pastInvitations.length > 0 && (
        <div className="past-events-list">
          <h4>Past Invitations History</h4>
          <div className="event-list-view">
            {pastInvitations.map(invitation => (
              <InvitationCardGuest key={invitation.event_id} invitation={invitation} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
